import React, { useState, useEffect, useCallback, useMemo, useRef, useLayoutEffect } from 'react';
import api from '../services/api';
import { fmtDate } from './TasksCommon';
import './Tasks.css';

const MONTHS_RU = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'];
const MONTHS_SHORT_RU = ['Янв', 'Фев', 'Мар', 'Апр', 'Май', 'Июн', 'Июл', 'Авг', 'Сен', 'Окт', 'Ноя', 'Дек'];

// Настройки зума: пиксели на день для каждого режима
const ZOOM_CONFIG = {
  day:   { pxPerDay: 22, colDays: 1, name: 'День' },
  week:  { pxPerDay: 28 / 7, colDays: 7, name: 'Неделя' },
  month: { pxPerDay: 90 / 30, colDays: 30, name: 'Месяц' },
};

function parseDate(s) {
  if (!s) return null;
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}

function daysBetween(a, b) {
  const ms = b - a;
  return Math.round(ms / (1000 * 60 * 60 * 24));
}

function addDays(d, n) {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

/** Смещение до понедельника (0..6). */
function daysToMonday(d) {
  return (d.getDay() + 6) % 7;
}

/** Начало недели (понедельник) для даты. */
function startOfWeek(d) {
  return addDays(d, -daysToMonday(d));
}

/** ISO-номер недели: 1..53. */
function isoWeek(d) {
  const t = new Date(d);
  t.setHours(0, 0, 0, 0);
  t.setDate(t.getDate() + 3 - ((t.getDay() + 6) % 7));
  const firstThursday = new Date(t.getFullYear(), 0, 4);
  const diff = (t - firstThursday) / 86400000;
  return 1 + Math.round((diff - 3 + ((firstThursday.getDay() + 6) % 7)) / 7);
}

function computeProjectSummary(rows, projectTitle, projectProgress) {
  // Ищем самую раннюю дату старта и самый поздний финиш среди всех задач с датами
  let minStart = null;
  let maxEnd = null;
  for (const r of rows) {
    if (r.start_date) {
      const d = new Date(r.start_date);
      if (!minStart || d < minStart) minStart = d;
    }
    if (r.end_date) {
      const d = new Date(r.end_date);
      if (!maxEnd || d > maxEnd) maxEnd = d;
    }
  }
  if (!minStart || !maxEnd) return null;
  const dur = Math.round((maxEnd - minStart) / (1000 * 60 * 60 * 24)) + 1;
  return {
    id: '__project_summary__',
    wbs_number: '0',
    title: projectTitle || 'Проект',
    parent_id: null,
    is_summary: true,
    is_milestone: false,
    is_project_summary: true,   // спецпризнак
    duration_days: dur,
    start_date: minStart.toISOString().slice(0, 10),
    end_date: maxEnd.toISOString().slice(0, 10),
    due_date: null,
    progress_pct: projectProgress || 0,
    status: 'in_progress',
    assignee: '',
    level: 0,
    has_children: true,
    rolled_up: [],
  };
}

export default function ProjectWbs({ projectId, onOpenTask, onAddChild }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(new Set());
  const [dragging, setDragging] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  const [rowGeom, setRowGeom] = useState({});
  const [barsHeight, setBarsHeight] = useState(0);
  const [hoveredTaskId, setHoveredTaskId] = useState(null);
  const [zoom, setZoom] = useState(() => localStorage.getItem('wbs_zoom') || 'week');
  const setZoomAndSave = (z) => {
    setZoom(z);
    localStorage.setItem('wbs_zoom', z);
  };
  const [showProjectSummary, setShowProjectSummary] = useState(
    () => localStorage.getItem('wbs_show_project_summary') === '1'
  );
  const toggleProjectSummary = () => {
    setShowProjectSummary((v) => {
      const nv = !v;
      localStorage.setItem('wbs_show_project_summary', nv ? '1' : '0');
      return nv;
    });
  };
  const leftBodyRef = useRef(null);
  const rightBodyRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getProjectWbs(projectId);
      setData(res.data);
    } catch { /* ignore */ }
    setLoading(false);
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const rawRows = data?.rows || [];
  const deps = data?.dependencies || [];
  const baseline = data?.baseline || null;
  const projectTitle = data?.project?.title;
  const projectProgress = data?.project?.progress;

  // Опционально добавляем «Итог проекта» первой строкой
  const rows = useMemo(() => {
    if (!showProjectSummary || rawRows.length === 0) return rawRows;
    const summary = computeProjectSummary(rawRows, projectTitle, projectProgress);
    if (!summary) return rawRows;
    return [summary, ...rawRows];
  }, [rawRows, showProjectSummary, projectTitle, projectProgress]);

  // Индексы для быстрого поиска: id → строка, id → predecessors/successors
  const rowsById = useMemo(() => Object.fromEntries(rows.map((r) => [r.id, r])), [rows]);
  const depsFrom = useMemo(() => {
    const m = {};
    for (const d of deps) {
      (m[d.from] = m[d.from] || []).push(d.to);
    }
    return m;
  }, [deps]);
  const depsTo = useMemo(() => {
    const m = {};
    for (const d of deps) {
      (m[d.to] = m[d.to] || []).push(d.from);
    }
    return m;
  }, [deps]);

  // Множество ID цепочки зависимостей от hovered
  const chainIds = useMemo(() => {
    if (!hoveredTaskId) return new Set();
    const set = new Set([hoveredTaskId]);
    // Все предшественники (рекурсивно)
    const upStack = [hoveredTaskId];
    while (upStack.length) {
      const x = upStack.pop();
      for (const p of depsTo[x] || []) if (!set.has(p)) { set.add(p); upStack.push(p); }
    }
    // Все преемники (рекурсивно)
    const downStack = [hoveredTaskId];
    while (downStack.length) {
      const x = downStack.pop();
      for (const s of depsFrom[x] || []) if (!set.has(s)) { set.add(s); downStack.push(s); }
    }
    return set;
  }, [hoveredTaskId, depsFrom, depsTo]);

  // Синхронизация высот строк слева и справа
  const syncHeights = () => {
    if (!leftBodyRef.current || !rightBodyRef.current) return;
    const lRows = leftBodyRef.current.querySelectorAll('tr.wbs-row');
    const rRows = rightBodyRef.current.querySelectorAll('.wbs-bar-row');
    const n = Math.min(lRows.length, rRows.length);

    // 1) Сброс к «натуральной» высоте
    for (let i = 0; i < n; i++) {
      const tds = lRows[i].querySelectorAll('td');
      tds.forEach((td) => { td.style.height = ''; });
      rRows[i].style.height = '';
    }
    // 2) Измеряем в двойном проходе (после reflow)
    const heights = [];
    for (let i = 0; i < n; i++) {
      const lh = lRows[i].getBoundingClientRect().height;
      const rh = rRows[i].getBoundingClientRect().height;
      heights.push(Math.ceil(Math.max(lh, rh)));
    }
    // 3) Выставляем ЦЕЛОЕ значение на TD (для таблицы это работает) и на bar-row
    for (let i = 0; i < n; i++) {
      const h = heights[i];
      const tds = lRows[i].querySelectorAll('td');
      tds.forEach((td) => { td.style.height = h + 'px'; });
      rRows[i].style.height = h + 'px';
    }
    // 4) Собираем позиции строк для отрисовки стрелок
    const geom = {};
    for (let i = 0; i < n; i++) {
      const taskId = Number(rRows[i].dataset.taskId);
      if (taskId) {
        geom[taskId] = {
          top: rRows[i].offsetTop,
          height: rRows[i].offsetHeight,
        };
      }
    }
    const newBarsH = rightBodyRef.current.offsetHeight;

    // Обновляем state только если реально изменилось (иначе бесконечный цикл)
    setRowGeom((prev) => {
      const keys = Object.keys(geom);
      if (Object.keys(prev).length !== keys.length) return geom;
      for (const k of keys) {
        if (!prev[k] || prev[k].top !== geom[k].top || prev[k].height !== geom[k].height) {
          return geom;
        }
      }
      return prev;
    });
    setBarsHeight((prev) => prev === newBarsH ? prev : newBarsH);
  };

  useLayoutEffect(() => {
    syncHeights();
    // Разрываем цикл ResizeObserver через requestAnimationFrame — иначе
    // при изменении размера контейнера (сайдбар, зум, вкладки) обновление
    // высот приводит к новому событию и получается бесконечный луп.
    let rafId = null;
    const scheduled = () => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        syncHeights();
      });
    };
    const onResize = () => scheduled();
    window.addEventListener('resize', onResize);
    let ro;
    if (typeof ResizeObserver !== 'undefined' && leftBodyRef.current) {
      ro = new ResizeObserver(() => scheduled());
      ro.observe(leftBodyRef.current);
    }
    return () => {
      window.removeEventListener('resize', onResize);
      if (rafId !== null) cancelAnimationFrame(rafId);
      if (ro) ro.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  });

  // Скрытые строки (свёрнутый родитель прячет всех детей рекурсивно)
  const hidden = useMemo(() => {
    const h = new Set();
    const collapsedIds = new Set(Array.from(collapsed));
    for (const r of rows) {
      let p = r.parent_id;
      while (p) {
        if (collapsedIds.has(p)) { h.add(r.id); break; }
        const parent = rows.find((x) => x.id === p);
        p = parent ? parent.parent_id : null;
      }
    }
    return h;
  }, [rows, collapsed]);

  const visibleRows = rows.filter((r) => !hidden.has(r.id));

  // Диапазон и колонки шкалы для оси Ганты — зависит от zoom
  const { minDate, columns, groups, totalDays, pxPerDay } = useMemo(() => {
    let mn = null, mx = null;
    for (const r of rows) {
      const s = parseDate(r.start_date);
      const e = parseDate(r.end_date);
      if (s && (!mn || s < mn)) mn = s;
      if (e && (!mx || e > mx)) mx = e;
      if (r.due_date) {
        const d = parseDate(r.due_date);
        if (d && (!mx || d > mx)) mx = d;
      }
    }
    if (!mn) mn = new Date();
    if (!mx) mx = addDays(mn, 30);

    if (zoom === 'day') {
      // Начало: минус несколько дней до понедельника
      const pxPd = ZOOM_CONFIG.day.pxPerDay;
      mn = startOfWeek(mn);
      const end = addDays(mx, 7);
      const nDays = daysBetween(mn, end) + 1;
      const days = [];
      for (let i = 0; i < nDays; i++) {
        days.push({ start: addDays(mn, i), label: String(addDays(mn, i).getDate()) });
      }
      // Группировка дней по месяцам
      const monthsArr = [];
      let cur = null;
      for (const d of days) {
        const label = `${MONTHS_RU[d.start.getMonth()]} ${d.start.getFullYear()}`;
        if (!cur || cur.label !== label) {
          if (cur) monthsArr.push(cur);
          cur = { label, cols: 1 };
        } else {
          cur.cols += 1;
        }
      }
      if (cur) monthsArr.push(cur);
      return { minDate: mn, columns: days, groups: monthsArr, totalDays: nDays, pxPerDay: pxPd };
    }

    if (zoom === 'month') {
      // Прижимаем к 1 числу месяца
      mn = new Date(mn.getFullYear(), mn.getMonth(), 1);
      const endMonth = new Date(mx.getFullYear(), mx.getMonth() + 1, 0);
      const cols = [];
      let cur = new Date(mn);
      while (cur <= endMonth) {
        cols.push({
          start: new Date(cur),
          label: MONTHS_SHORT_RU[cur.getMonth()],
        });
        cur = new Date(cur.getFullYear(), cur.getMonth() + 1, 1);
      }
      // Группы = годы
      const groupsArr = [];
      let g = null;
      for (const c of cols) {
        const yr = c.start.getFullYear();
        if (!g || g.label !== yr) {
          if (g) groupsArr.push(g);
          g = { label: yr, cols: 1 };
        } else {
          g.cols += 1;
        }
      }
      if (g) groupsArr.push(g);
      const totalDaysCalc = daysBetween(mn, endMonth) + 1;
      const pxPd = 90 / 30;
      return { minDate: mn, columns: cols, groups: groupsArr, totalDays: totalDaysCalc, pxPerDay: pxPd };
    }

    // week (default)
    mn = startOfWeek(mn);
    let end = addDays(mx, 7 - ((daysToMonday(mx) + 1) % 7 || 7));
    end = addDays(end, 0);
    const totalDaysCalc = daysBetween(mn, end);
    const nWeeks = Math.ceil(totalDaysCalc / 7) + 1;
    const weeksArr = [];
    for (let i = 0; i < nWeeks; i++) {
      const wStart = addDays(mn, i * 7);
      weeksArr.push({ start: wStart, label: String(isoWeek(wStart)) });
    }
    const monthsArr = [];
    let cur = null;
    for (const w of weeksArr) {
      const label = `${MONTHS_RU[w.start.getMonth()]} ${w.start.getFullYear()}`;
      if (!cur || cur.label !== label) {
        if (cur) monthsArr.push(cur);
        cur = { label, cols: 1 };
      } else {
        cur.cols += 1;
      }
    }
    if (cur) monthsArr.push(cur);
    return { minDate: mn, columns: weeksArr, groups: monthsArr, totalDays: nWeeks * 7, pxPerDay: 28 / 7 };
  }, [rows, zoom]);

  const colPx = zoom === 'day' ? ZOOM_CONFIG.day.pxPerDay
    : zoom === 'month' ? 90
    : 28;
  const totalWeeks = columns.length;   // legacy: totalWeeks теперь = число колонок

  const toggleCollapse = (id) => {
    setCollapsed((prev) => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id);
      else s.add(id);
      return s;
    });
  };

  const handleDragStart = (id) => (e) => {
    setDragging(id);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(id));
  };

  const handleDragOver = (targetRow) => (e) => {
    if (!dragging || dragging === targetRow.id) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const thirds = rect.height / 3;
    let mode;
    if (y < thirds) mode = 'before';
    else if (y > 2 * thirds) mode = 'after';
    else mode = targetRow.has_children || targetRow.is_summary ? 'child' : 'after';
    setDropTarget({ id: targetRow.id, mode });
  };

  const handleDragLeave = () => {
    // Не очищаем сразу — dragOver у другого элемента подхватит
  };

  const handleDrop = (targetRow) => async (e) => {
    e.preventDefault();
    if (!dragging || !dropTarget) return;
    const dragId = dragging;
    const { id: targetId, mode } = dropTarget;
    setDragging(null);
    setDropTarget(null);

    if (dragId === targetId) return;

    // Определяем новый parent и индекс
    let newParentId = null;
    let newIndex = 0;
    if (mode === 'child') {
      newParentId = targetId;
      newIndex = 999; // в конец
    } else {
      const target = rows.find((x) => x.id === targetId);
      newParentId = target ? target.parent_id : null;
      // Соберём сиблингов
      const siblings = rows.filter((x) => x.parent_id === newParentId && x.id !== dragId);
      const idx = siblings.findIndex((x) => x.id === targetId);
      newIndex = mode === 'before' ? idx : idx + 1;
    }

    try {
      await api.reorderWbsTask(projectId, {
        task_id: dragId,
        new_parent_id: newParentId,
        new_index: newIndex,
      });
      await load();
    } catch (err) {
      alert('Не удалось переставить: ' + (err.response?.data?.detail || err.message));
    }
  };

  // Позиция бара в пикселях — универсальная (учитывает zoom через pxPerDay).
  const barStyle = (r) => {
    const s = parseDate(r.start_date);
    const e = parseDate(r.end_date);
    if (!s || !e) return null;
    const startDaysFromMin = daysBetween(minDate, s);
    const endDaysFromMin = daysBetween(minDate, e) + 1;
    const left = startDaysFromMin * pxPerDay;
    const width = Math.max(4, (endDaysFromMin - startDaysFromMin) * pxPerDay);
    return { left, width };
  };

  // Позиция baseline-бара (снизу под задачей)
  const baselineBarStyle = (r) => {
    if (!r.baseline_start || !r.baseline_end) return null;
    const s = parseDate(r.baseline_start);
    const e = parseDate(r.baseline_end);
    if (!s || !e) return null;
    const startDaysFromMin = daysBetween(minDate, s);
    const endDaysFromMin = daysBetween(minDate, e) + 1;
    const left = startDaysFromMin * pxPerDay;
    const width = Math.max(4, (endDaysFromMin - startDaysFromMin) * pxPerDay);
    return { left, width };
  };

  const isOverdue = (r) => {
    if (!r.due_date) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = parseDate(r.due_date);
    return due && due < today && r.status !== 'done' && r.status !== 'cancelled';
  };

  if (loading) return <div className="tasks-empty">Загрузка…</div>;
  if (!data) return <div className="tasks-empty">Нет данных</div>;
  if (rows.length === 0) {
    return (
      <div className="tasks-empty">
        В проекте нет задач. Добавьте задачи через «+ Новая задача» — они появятся в WBS-плане.
      </div>
    );
  }

  const chartWidth = columns.length * colPx;

  return (
    <>
    {baseline && (
      <div className="wbs-baseline-info">
        📌 Активный базовый план: <strong>{baseline.name}</strong>
        <span style={{ color: '#6b7280', fontSize: 12, marginLeft: 8 }}>
          зафиксирован {new Date(baseline.created_at).toLocaleDateString('ru-RU')}
        </span>
        <span style={{ marginLeft: 16, color: '#6b7280', fontSize: 12 }}>
          В строках Ганты серым пунктиром показан план, цветным — факт.
        </span>
      </div>
    )}
    <div style={{ marginBottom: 8, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
      <label style={{ cursor: 'pointer', fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <input
          type="checkbox"
          checked={showProjectSummary}
          onChange={toggleProjectSummary}
        />
        Показать итог проекта (Project Summary)
      </label>
      <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 0, border: '1px solid #d1d5db', borderRadius: 6, overflow: 'hidden' }}>
        {['day', 'week', 'month'].map((z) => (
          <button
            key={z}
            onClick={() => setZoomAndSave(z)}
            style={{
              padding: '6px 14px',
              fontSize: 13,
              background: zoom === z ? '#2563eb' : '#fff',
              color: zoom === z ? '#fff' : '#111',
              border: 'none',
              borderRight: z !== 'month' ? '1px solid #d1d5db' : 'none',
              cursor: 'pointer',
              fontWeight: zoom === z ? 600 : 400,
            }}
          >
            {ZOOM_CONFIG[z].name}
          </button>
        ))}
      </span>
    </div>
    <div className="wbs-wrap">
      {/* Таблица слева */}
      <div className="wbs-table-scroll">
        <table className="wbs-table">
          <thead>
            <tr>
              <th style={{ width: 60 }}>№</th>
              <th style={{ minWidth: 240 }}>Название</th>
              <th style={{ width: 90 }}>Длит.</th>
              <th style={{ width: 90 }}>Начало</th>
              <th style={{ width: 90 }}>Финиш</th>
              <th style={{ minWidth: 140 }}>Ответственный</th>
              <th style={{ width: 60 }}>%</th>
              <th style={{ width: 90 }}>Действия</th>
            </tr>
          </thead>
          <tbody ref={leftBodyRef}>
            {visibleRows.map((r) => (
              <tr
                key={r.id}
                className={`wbs-row ${r.is_summary ? 'summary' : ''} ${r.is_project_summary ? 'project-summary' : ''} ${isOverdue(r) ? 'overdue' : ''} ${dropTarget?.id === r.id ? 'drop-' + dropTarget.mode : ''} ${chainIds.has(r.id) ? 'chain' : ''} ${hoveredTaskId && !chainIds.has(r.id) ? 'faded' : ''}`}
                onClick={() => !r.is_project_summary && onOpenTask && onOpenTask(r.id)}
                title={r.title}
                draggable={!r.is_project_summary}
                onDragStart={r.is_project_summary ? undefined : handleDragStart(r.id)}
                onDragOver={r.is_project_summary ? undefined : handleDragOver(r)}
                onDragLeave={r.is_project_summary ? undefined : handleDragLeave}
                onDrop={r.is_project_summary ? undefined : handleDrop(r)}
                onDragEnd={r.is_project_summary ? undefined : () => { setDragging(null); setDropTarget(null); }}
                onMouseEnter={() => !r.is_project_summary && setHoveredTaskId(r.id)}
                onMouseLeave={() => !r.is_project_summary && setHoveredTaskId(null)}
              >
                <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{r.wbs_number}</td>
                <td>
                  <span style={{ paddingLeft: r.level * 16 }}>
                    {r.has_children && (
                      <span
                        className="wbs-toggle"
                        onClick={(e) => { e.stopPropagation(); toggleCollapse(r.id); }}
                      >
                        {collapsed.has(r.id) ? '▶' : '▼'}
                      </span>
                    )}
                    {!r.has_children && r.is_milestone && <span style={{ marginRight: 6 }}>◆</span>}
                    <span style={{ fontWeight: r.is_summary ? 700 : 400 }}>{r.title}</span>
                  </span>
                </td>
                <td>{r.duration_days ? `${r.duration_days} дн.` : '—'}</td>
                <td>{r.start_date ? fmtDate(r.start_date) : '—'}</td>
                <td>{r.end_date ? fmtDate(r.end_date) : '—'}</td>
                <td style={{ fontSize: 12 }}>{r.assignee || '—'}</td>
                <td>
                  <span style={{
                    fontWeight: 600,
                    color: r.progress_pct === 100 ? '#16a34a' : r.progress_pct > 0 ? '#f59e0b' : '#6b7280',
                  }}>
                    {r.progress_pct}%
                  </span>
                  {r.baseline_delta_days !== undefined && r.baseline_delta_days !== 0 && (
                    <span
                      className="wbs-baseline-delta"
                      style={{
                        marginLeft: 6, fontSize: 11, fontWeight: 600,
                        color: r.baseline_delta_days > 0 ? '#b91c1c' : '#16a34a',
                      }}
                      title={r.baseline_delta_days > 0
                        ? `Отставание от плана: ${r.baseline_delta_days} дн.`
                        : `Опережение плана: ${-r.baseline_delta_days} дн.`}
                    >
                      {r.baseline_delta_days > 0 ? '+' : ''}{r.baseline_delta_days}д
                    </span>
                  )}
                  {r.deadline_status === 'missed' && (
                    <span
                      className="wbs-deadline-warn missed"
                      title={`Нарушение дедлайна! Опоздание на ${-r.deadline_days} дн. (дедлайн ${fmtDate(r.deadline)})`}
                    >⚠</span>
                  )}
                  {r.deadline_status === 'at_risk' && (
                    <span
                      className="wbs-deadline-warn at-risk"
                      title={`Риск нарушения дедлайна. Запас ${r.deadline_days} дн. (дедлайн ${fmtDate(r.deadline)})`}
                    >⚠</span>
                  )}
                </td>
                <td className="wbs-actions">
                  <button
                    className="wbs-add-btn"
                    title="Добавить подзадачу"
                    onClick={(e) => { e.stopPropagation(); onAddChild && onAddChild(r.id, 'task'); }}
                  >+ 📋</button>
                  <button
                    className="wbs-add-btn"
                    title="Добавить веху"
                    onClick={(e) => { e.stopPropagation(); onAddChild && onAddChild(r.id, 'milestone'); }}
                  >+ ◆</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Диаграмма справа */}
      <div className="wbs-chart-scroll">
        <div className="wbs-chart" style={{ width: chartWidth }}>
          {/* Шапка: группы (месяц/год) + колонки (дни/недели/месяцы) */}
          <div className="wbs-chart-header">
            <div className="wbs-months">
              {groups.map((m, i) => (
                <div key={i} className="wbs-month" style={{ width: m.cols * colPx }}>
                  {m.label}
                </div>
              ))}
            </div>
            <div className="wbs-weeks">
              {columns.map((w, i) => (
                <div
                  key={i}
                  className="wbs-week"
                  style={{ width: colPx }}
                  title={w.start.toLocaleDateString('ru-RU')}
                >
                  {w.label}
                </div>
              ))}
            </div>
          </div>
          {/* Полосы задач */}
          <div className="wbs-bars" ref={rightBodyRef} style={{ position: 'relative' }}>
            {visibleRows.map((r) => {
              const bs = barStyle(r);
              const bbs = baselineBarStyle(r);
              const overdue = isOverdue(r);
              const isChain = chainIds.has(r.id);
              const isFaded = hoveredTaskId && !isChain;
              return (
                <div
                  key={r.id}
                  className={`wbs-bar-row ${isFaded ? 'faded' : ''} ${isChain ? 'chain' : ''}`}
                  data-task-id={r.id}
                  onMouseEnter={() => setHoveredTaskId(r.id)}
                  onMouseLeave={() => setHoveredTaskId(null)}
                >
                  {/* Фон-разлиновка */}
                  {columns.map((_, i) => (
                    <div
                      key={i}
                      className="wbs-week-bg"
                      style={{ width: colPx }}
                    />
                  ))}
                  {/* Baseline-бар (снизу, серый пунктир) */}
                  {bbs && (
                    <div
                      className="wbs-baseline-bar"
                      style={bbs}
                      title={`План: ${fmtDate(r.baseline_start)} — ${fmtDate(r.baseline_end)}`}
                    />
                  )}
                  {/* Бар задачи */}
                  {bs && (
                    r.is_milestone ? (
                      <div
                        className="wbs-milestone"
                        style={{ left: bs.left - 8 }}
                        title={`${r.title} — ${fmtDate(r.start_date)}`}
                      />
                    ) : r.is_summary ? (
                      <div className={`wbs-summary-bar ${r.is_project_summary ? 'project-summary' : ''}`} style={bs} title={r.title}>
                        <div className="wbs-summary-cap left"></div>
                        <div className="wbs-summary-body"></div>
                        <div className="wbs-summary-cap right"></div>
                        {/* Rolled Up дети — показываются когда фаза свёрнута */}
                        {collapsed.has(r.id) && r.rolled_up && r.rolled_up.map((child) => {
                          const cs = parseDate(child.start_date);
                          const ce = parseDate(child.end_date);
                          if (!cs || !ce) return null;
                          const cLeft = daysBetween(minDate, cs) * pxPerDay - bs.left;
                          const cWidth = Math.max(3, (daysBetween(cs, ce) + 1) * pxPerDay);
                          if (child.is_milestone) {
                            return (
                              <div
                                key={child.id}
                                className="rolled-up-milestone"
                                style={{ left: cLeft - 4 }}
                                title="Свёрнутая веха"
                              />
                            );
                          }
                          return (
                            <div
                              key={child.id}
                              className={`rolled-up-task ${child.is_critical ? 'critical' : ''}`}
                              style={{ left: cLeft, width: cWidth }}
                              title="Свёрнутая задача"
                            />
                          );
                        })}
                      </div>
                    ) : (
                      <div
                        className={`wbs-task-bar ${overdue ? 'overdue' : ''} ${r.is_critical ? 'critical' : ''}`}
                        style={bs}
                        title={`${r.title} — ${r.progress_pct}%${r.is_critical ? ' — критический путь' : ''}`}
                      >
                        {/* Дедлайн — зелёная стрелка */}
                        {r.deadline && (() => {
                          const dl = parseDate(r.deadline);
                          if (!dl) return null;
                          const dlPos = daysBetween(minDate, dl) * pxPerDay - bs.left;
                          return (
                            <div
                              className="wbs-deadline-arrow"
                              style={{ left: dlPos - 6 }}
                              title={`Дедлайн: ${fmtDate(r.deadline)}`}
                            />
                          );
                        })()}
                        <div
                          className="wbs-task-progress"
                          style={{ width: `${r.progress_pct}%` }}
                        />
                        <span className="wbs-task-label">{r.title}</span>
                      </div>
                    )
                  )}
                </div>
              );
            })}
            {/* SVG-стрелки зависимостей — поверх баров */}
            {deps.length > 0 && Object.keys(rowGeom).length > 0 && (
              <svg
                width={chartWidth}
                height={barsHeight || 800}
                style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none', zIndex: 5, overflow: 'visible' }}
              >
                <defs>
                  <marker id="wbs-arr" viewBox="0 0 10 10" refX="9" refY="5"
                          markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                    <path d="M 0 0 L 10 5 L 0 10 z" fill="#1f2937" />
                  </marker>
                  <marker id="wbs-arr-crit" viewBox="0 0 10 10" refX="9" refY="5"
                          markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                    <path d="M 0 0 L 10 5 L 0 10 z" fill="#b91c1c" />
                  </marker>
                </defs>
                {deps.map((d) => {
                  const fromRow = visibleRows.find((x) => x.id === d.from);
                  const toRow = visibleRows.find((x) => x.id === d.to);
                  if (!fromRow || !toRow) return null;
                  const fromBar = barStyle(fromRow);
                  const toBar = barStyle(toRow);
                  if (!fromBar || !toBar) return null;
                  const gf = rowGeom[d.from];
                  const gt = rowGeom[d.to];
                  if (!gf || !gt) return null;

                  // Y-центр бара (бар начинается 5px от верха строки, высота 16px)
                  const barCenterOffset = 13;
                  const fromY = gf.top + barCenterOffset;
                  const toY = gt.top + barCenterOffset;
                  const fromX = fromBar.left + fromBar.width;
                  const toX = toBar.left;

                  // Красный если ОБА конца на критическом пути
                  const isCritical = !!(fromRow.is_critical && toRow.is_critical);
                  const color = isCritical ? '#b91c1c' : '#1f2937';
                  const marker = isCritical ? 'wbs-arr-crit' : 'wbs-arr';

                  // Маршрут по правилам MS Project:
                  // 1. Если преемник ниже И правее — L-образная: → ↓ →
                  // 2. Если преемник выше И правее — L-образная: → ↑ →
                  // 3. Если преемник левее (нарушение порядка) — обход снизу
                  const goingDown = toY > fromY;
                  const gap = 4;  // отступ от бара
                  let path;
                  if (toX >= fromX + gap) {
                    // Нормальный случай: преемник правее
                    const midX = toX - gap;
                    path = `M ${fromX} ${fromY} L ${midX} ${fromY} L ${midX} ${toY} L ${toX - 2} ${toY}`;
                  } else {
                    // Преемник начинается раньше окончания предшественника — обход
                    const detourY = goingDown
                      ? gf.top + gf.height + 4    // проходим под предшественником
                      : gf.top - 4;               // над предшественником
                    path = `M ${fromX} ${fromY} L ${fromX + gap} ${fromY} L ${fromX + gap} ${detourY} L ${toX - gap} ${detourY} L ${toX - gap} ${toY} L ${toX - 2} ${toY}`;
                  }

                  // Приглушение / подсветка
                  const inChain = chainIds.has(d.from) && chainIds.has(d.to);
                  const opacity = hoveredTaskId ? (inChain ? 1 : 0.15) : 1;
                  const strokeW = inChain ? '2' : '1.3';

                  return (
                    <path
                      key={`${d.from}-${d.to}`}
                      d={path}
                      stroke={color}
                      strokeWidth={strokeW}
                      strokeOpacity={opacity}
                      fill="none"
                      markerEnd={`url(#${marker})`}
                    />
                  );
                })}
              </svg>
            )}
          </div>
        </div>
      </div>
    </div>
    {/* Легенда */}
    <div className="wbs-legend">
      <div className="wbs-legend-title">Обозначения:</div>
      <div className="wbs-legend-grid">
        <div className="wbs-legend-item">
          <span className="lg-sample lg-task">
            <span className="lg-task-fill" style={{ width: '60%' }} />
          </span>
          <span>Задача (синий бар с прогрессом)</span>
        </div>
        <div className="wbs-legend-item">
          <span className="lg-sample lg-task lg-overdue">
            <span className="lg-task-fill lg-overdue-fill" style={{ width: '30%' }} />
          </span>
          <span>Просроченная задача</span>
        </div>
        <div className="wbs-legend-item">
          <span className="lg-sample lg-critical">
            <span className="lg-task-fill lg-critical-fill" style={{ width: '50%' }} />
          </span>
          <span>Критический путь (задержка = задержка проекта)</span>
        </div>
        <div className="wbs-legend-item">
          <span className="lg-sample lg-summary">
            <span className="lg-summary-cap-l" />
            <span className="lg-summary-body" />
            <span className="lg-summary-cap-r" />
          </span>
          <span>Фаза (Summary — родитель)</span>
        </div>
        <div className="wbs-legend-item">
          <span className="lg-sample lg-milestone-wrap">
            <span className="lg-milestone" />
          </span>
          <span>Веха (Milestone — контрольная точка)</span>
        </div>
        <div className="wbs-legend-item">
          <span className="lg-sample lg-row-overdue"></span>
          <span>Красный фон строки = просрочка</span>
        </div>
        <div className="wbs-legend-item">
          <span className="lg-sample lg-row-summary"></span>
          <span>Синяя строка = родитель (агрегирует детей)</span>
        </div>
      </div>
    </div>
    </>
  );
}
